import { create } from 'zustand';

// Health check URL - always hit the API directly (not through Vite proxy)
// so we detect API-down even if the proxy masks the failure.
const healthUrl = `${import.meta.env.VITE_API_BASE_URL}/health`;

interface ApiHealthState {
  isApiDown: boolean;
  lastError: string;
  checkCount: number;
  isChecking: boolean;
  downSince: number | null;
  lastCheckTime: number | null;
  recovered: boolean;
  setApiDown: (down: boolean, error?: string) => void;
  checkHealth: () => Promise<void>;
}

// Track consecutive failures to avoid false positives from single timeouts
let consecutiveFailures = 0;
const FAILURE_THRESHOLD = 2;

export const useApiHealthStore = create<ApiHealthState>((set, get) => ({
  isApiDown: false,
  lastError: '',
  checkCount: 0,
  isChecking: false,
  downSince: null,
  lastCheckTime: null,
  recovered: false,

  setApiDown: (down, error = '') => {
    if (down) {
      consecutiveFailures++;
      if (consecutiveFailures >= FAILURE_THRESHOLD) {
        set((prev) => ({
          isApiDown: true,
          lastError: error,
          downSince: prev.downSince ?? Date.now(),
        }));
      }
    } else {
      consecutiveFailures = 0;
      if (get().isApiDown) {
        // Show recovered state briefly before dismissing
        set({ recovered: true, isChecking: false });
        setTimeout(() => {
          set({ isApiDown: false, lastError: '', downSince: null, recovered: false, checkCount: 0, lastCheckTime: null });
        }, 1500);
      } else {
        set({ isApiDown: false, lastError: '', downSince: null });
      }
    }
  },

  checkHealth: async () => {
    if (get().isChecking) return;
    set((prev) => ({ isChecking: true, checkCount: prev.checkCount + 1, lastCheckTime: Date.now() }));
    try {
      // Use fetch (not apiClient) to avoid circular dependency with client.ts
      await fetch(healthUrl, {
        signal: AbortSignal.timeout(8000),
      });
      // Any HTTP response (even 400/405) means the server is reachable.
      // Only network errors (fetch throws) indicate the server is truly down.
      consecutiveFailures = 0;
      if (get().isApiDown) {
        set({ recovered: true, isChecking: false });
        setTimeout(() => {
          set({ isApiDown: false, lastError: '', downSince: null, recovered: false, checkCount: 0, lastCheckTime: null });
        }, 1500);
      } else {
        set({ isApiDown: false, lastError: '', isChecking: false });
      }
    } catch {
      set({ isChecking: false });
      // Don't change isApiDown here - it was already set by the interceptor
    }
  },
}));

/**
 * Called from the axios response interceptor when a network error
 * (no response at all) or 503 is detected.
 */
export function reportApiError(error: string) {
  useApiHealthStore.getState().setApiDown(true, error);
}

/**
 * Called from the axios response interceptor on any successful response,
 * which means the API is reachable again.
 */
export function reportApiSuccess() {
  if (useApiHealthStore.getState().isApiDown) {
    useApiHealthStore.getState().setApiDown(false);
  }
}
